import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { customerService } from '../../services';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

const CustomerList = () => {
  const navigate = useNavigate();
  
  // State for customers data
  const [customers, setCustomers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination and filtering state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('');
  
  // Export menu state
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  
  // Fetch customers data
  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Convert page for API (1-based)
      const apiPage = page + 1;
      
      // Prepare filter object
      const filter = {};
      if (searchTerm) {
        if (filterBy === 'email') {
          filter.email = searchTerm;
        } else if (filterBy === 'country') {
          filter.country = searchTerm;
        } else {
          // Default to name search
          filter.name = searchTerm;
        }
      }
      
      const response = await customerService.getCustomers({
        page: apiPage,
        limit: rowsPerPage,
        sort: '-createdAt', // Sort by newest first
        filter
      });
      
      setCustomers(response.customers || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };
  
  // Load customers on initial render and when pagination/filtering changes
  useEffect(() => {
    fetchCustomers();
  }, [page, rowsPerPage, searchTerm, filterBy]);
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle search
  const handleSearch = (event) => {
    if (event.key === 'Enter') {
      setSearchTerm(event.target.value);
      setPage(0);
    }
  };
  
  // Handle filter change
  const handleFilterChange = (event) => {
    setFilterBy(event.target.value);
    setPage(0);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Handle export menu open
  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };
  
  // Handle export menu close
  const handleExportClose = () => {
    setExportAnchorEl(null);
  };
  
  // Handle export customers
  const handleExportCustomers = async (format = 'csv') => {
    try {
      setLoading(true);
      
      // Prepare filter for export
      const filter = {};
      if (searchTerm) {
        filter.search = searchTerm;
      }
      
      const blob = await customerService.exportCustomers({
        format,
        filter
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customers-export-${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Close the menu
      handleExportClose();
    } catch (err) {
      setError(`Failed to export customers: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Customers
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your customer information and records
        </Typography>
      </Box>
      
      {/* Action buttons and search */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            component={Link}
            to="/admin/customers/new"
          >
            Add Customer
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<FileDownloadIcon />}
            onClick={handleExportClick}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportClose}
          >
            <MenuItem onClick={() => handleExportCustomers('csv')}>Export as CSV</MenuItem>
            <MenuItem onClick={() => handleExportCustomers('excel')}>Export as Excel</MenuItem>
          </Menu>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            placeholder="Search customers..."
            size="small"
            onKeyPress={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
          />
          <TextField
            select
            size="small"
            value={filterBy}
            onChange={handleFilterChange}
            SelectProps={{
              native: true,
            }}
            sx={{ minWidth: 120 }}
          >
            <option value="">Name</option>
            <option value="email">Email</option>
            <option value="country">Country</option>
          </TextField>
        </Box>
      </Box>
      
      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Customer table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="customers table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No customers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((customer) => (
                    <TableRow key={customer._id}>
                      <TableCell>
                        {customer.firstName} {customer.lastName}
                      </TableCell>
                      <TableCell>{customer.email}</TableCell>
                      <TableCell>{customer.country}</TableCell>
                      <TableCell>{formatDate(customer.createdAt)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={customer.hasPayment ? "Active" : "New"} 
                          color={customer.hasPayment ? "success" : "primary"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tooltip title="View">
                            <IconButton
                              onClick={() => navigate(`/admin/customers/${customer._id}`)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => navigate(`/admin/customers/${customer._id}/edit`)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>
    </Container>
  );
};

export default CustomerList;